import { useState, useCallback } from "react";
import { ALL_LEAGUES } from "../constants";
import { useIsMobile } from "../useIsMobile";

const API_URL = process.env.REACT_APP_API_URL || "https://web-production-c0e77.up.railway.app";

const SUPPORTED_LEAGUES = [
  "Premier League","Championship","La Liga","Bundesliga",
  "Serie A","Ligue 1","Eredivisie","Süper Lig","Pro League","Primeira Liga",
];

const SIDES = [
  { value: "home", label: "Ev Sahibi", color: "#16a34a" },
  { value: "away", label: "Deplasman", color: "#2563eb" },
  { value: "draw", label: "Beraberlik", color: "#d97706" },
];

const SEASONS = ["","2020-21","2021-22","2022-23","2023-24","2024-25","2025-26"];

// ─── Yardımcı bileşenler ──────────────────────────────────────────────────────
const SapmaTag = ({ val, size = 12 }) => {
  const abs = Math.abs(val);
  if (abs < 1) return <span style={{ color: "#9ca3af", fontSize: size }}>{val > 0 ? "+" : ""}{val}pp</span>;
  const col = abs >= 5
    ? (val > 0 ? { bg: "#fffbeb", txt: "#92400e" } : { bg: "#fef2f2", txt: "#991b1b" })
    : (val > 0 ? { bg: "#f0fdf4", txt: "#166534" } : { bg: "#eff6ff", txt: "#1d4ed8" });
  return (
    <span style={{ background: col.bg, color: col.txt, padding: "2px 7px",
      borderRadius: 4, fontWeight: 700, fontSize: size }}>
      {val > 0 ? "+" : ""}{val}pp
    </span>
  );
};

const MiniTrend = ({ trend }) => {
  if (!trend || trend.length < 2) return <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>;
  const last = trend[trend.length - 1].sapma;
  const first = trend[0].sapma;
  const dir = last - first;
  const arrow = Math.abs(dir) < 1 ? "→" : dir > 0 ? "↗" : "↘";
  const col = Math.abs(dir) < 1 ? "#9ca3af" : dir > 0 ? "#16a34a" : "#dc2626";
  return (
    <span style={{ color: col, fontSize: 13, fontWeight: 700 }} title={trend.map(t => `${t.season}: ${t.sapma > 0 ? "+" : ""}${t.sapma}pp`).join(" | ")}>
      {arrow} {trend.map(t => `${t.sapma > 0 ? "+" : ""}${t.sapma}`).join(" → ")}
    </span>
  );
};

const DistBar = ({ H, D, A, highlight }) => {
  const colors = { H: "#16a34a", D: "#d97706", A: "#2563eb" };
  return (
    <div style={{ display: "flex", height: 18, borderRadius: 3, overflow: "hidden", gap: 1, minWidth: 100 }}>
      {[["H", H], ["D", D], ["A", A]].map(([k, v]) => (
        <div key={k} style={{
          width: `${v}%`, background: colors[k],
          opacity: highlight === k ? 1 : 0.35,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {v >= 15 && <span style={{ fontSize: 9, fontWeight: 700, color: "#fff" }}>{v}</span>}
        </div>
      ))}
    </div>
  );
};

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────
export default function OddsAnalysis() {
  const isMobile = useIsMobile();
  const [league,  setLeague]  = useState("Premier League");
  const [season,  setSeason]  = useState("");
  const [side,    setSide]    = useState("home");
  const [minN,    setMinN]    = useState(20);
  const [loading, setLoading] = useState(false);
  const [data,    setData]    = useState(null);
  const [error,   setError]   = useState(null);
  const [activeTab, setActiveTab] = useState("bands");
  const [teamSort, setTeamSort] = useState("sapma_abs");
  const [teamFilter, setTeamFilter] = useState("");

  const sideInfo = SIDES.find(s => s.value === side);

  const run = useCallback(async () => {
    setLoading(true); setError(null); setData(null);
    try {
      const params = new URLSearchParams({ league, side, min_n: minN });
      if (season) params.set("season", season);
      const res = await fetch(`${API_URL}/api/analytics/odds-bias?${params}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "API hatası");
      }
      setData(await res.json());
      setActiveTab("bands");
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [league, season, side, minN]);

  // Sıralanmış takım listesi
  const sortedTeams = data?.team_bias
    ? [...data.team_bias]
        .filter(t => !teamFilter || t.team.toLowerCase().includes(teamFilter.toLowerCase()))
        .sort((a, b) => {
          if (teamSort === "sapma_abs") return Math.abs(b.sapma) - Math.abs(a.sapma);
          if (teamSort === "sapma")     return b.sapma - a.sapma;
          if (teamSort === "sapma_neg") return a.sapma - b.sapma;
          if (teamSort === "n")         return b.n - a.n;
          return 0;
        })
    : [];

  const highlightFTR = { home: "H", draw: "D", away: "A" }[side];

  return (
    <div style={{ padding: "0 0 40px" }}>

      {/* ── Filtre paneli ── */}
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
        padding: "16px 20px", marginBottom: 20,
        display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end",
      }}>
        {/* Lig */}
        <div>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.8 }}>Lig</div>
          <select value={league} onChange={e => setLeague(e.target.value)}
            style={{ fontSize: 13, padding: "6px 12px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", width: 200 }}>
            {SUPPORTED_LEAGUES.filter(l => ALL_LEAGUES.includes(l) || true).map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        {/* Sezon */}
        <div>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.8 }}>Sezon</div>
          <select value={season} onChange={e => setSeason(e.target.value)}
            style={{ fontSize: 13, padding: "6px 12px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff" }}>
            {SEASONS.map(s => <option key={s} value={s}>{s || "Tüm Sezonlar"}</option>)}
          </select>
        </div>

        {/* Taraf */}
        <div>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.8 }}>Analiz Tarafı</div>
          <div style={{ display: "flex", gap: 4 }}>
            {SIDES.map(s => (
              <button key={s.value} onClick={() => setSide(s.value)} style={{
                padding: "6px 14px", fontSize: 13, borderRadius: 6, cursor: "pointer",
                border: `1px solid ${side === s.value ? s.color : "#e5e7eb"}`,
                background: side === s.value ? s.color : "#fff",
                color: side === s.value ? "#fff" : "#374151",
                fontWeight: side === s.value ? 700 : 400,
              }}>{s.label}</button>
            ))}
          </div>
        </div>

        {/* Min maç */}
        <div>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.8 }}>Min. Maç</div>
          <input type="number" value={minN} onChange={e => setMinN(+e.target.value)}
            min={5} max={100} style={{ width: 70, fontSize: 13, padding: "6px 10px",
              border: "1px solid #e5e7eb", borderRadius: 6, textAlign: "center" }} />
        </div>

        <button onClick={run} disabled={loading} style={{
          padding: "8px 24px", fontSize: 14, fontWeight: 700, borderRadius: 6,
          border: "none", background: loading ? "#9ca3af" : sideInfo.color,
          color: "#fff", cursor: loading ? "default" : "pointer", marginTop: 18,
        }}>
          {loading ? "⏳ Yükleniyor..." : "🔍 Analiz Et"}
        </button>

        {data && (
          <div style={{ marginTop: 18, fontSize: 12, color: "#6b7280" }}>
            {data.total_matches.toLocaleString("tr-TR")} maç ·{" "}
            {data.seasons_included.join(", ")}
          </div>
        )}
      </div>

      {/* Hata */}
      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6,
          padding: "12px 16px", color: "#dc2626", marginBottom: 16, fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}

      {/* Sonuçlar */}
      {data && (
        <div>
          {/* Tab nav */}
          <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e5e7eb", marginBottom: 20 }}>
            {[
              ["bands",     "📊 Olasılık Bantları"],
              ["direction", "🧭 Yön Analizi"],
              ["teams",     "👥 Takım Sapmaları"],
            ].map(([key, lbl]) => (
              <button key={key} onClick={() => setActiveTab(key)} style={{
                padding: "8px 18px", fontSize: 13, border: "none", background: "transparent",
                borderBottom: activeTab === key ? `2px solid ${sideInfo.color}` : "2px solid transparent",
                color: activeTab === key ? sideInfo.color : "#6b7280",
                fontWeight: activeTab === key ? 700 : 500, cursor: "pointer", marginBottom: -2,
              }}>{lbl}</button>
            ))}
          </div>

          {/* ════ TAB: Olasılık Bantları ════ */}
          {activeTab === "bands" && (
            <div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14 }}>
                Piyasa {sideInfo.label.toLowerCase()} galibiyetine verdiği olasılık bantlarına göre gerçek sonuç dağılımı.
                Sapma = Gerçek% − Piyasa%.
              </div>
              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      {["Piyasa Olasılık Bandı","N","Ort. Piyasa%","Gerçek%","Sapma","Sonuç Dağılımı (Ev/Ber/Dep)"].map((h, i) => (
                        <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11,
                          color: "#6b7280", letterSpacing: 0.8, textTransform: "uppercase",
                          borderBottom: "1px solid #e5e7eb", fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.prob_bands.map((b, i) => (
                      <tr key={b.band} style={{ borderBottom: "1px solid #f3f4f6",
                        background: i % 2 === 1 ? "#fafafa" : "#fff" }}>
                        <td style={{ padding: "10px 14px", fontWeight: 600 }}>%{b.band}</td>
                        <td style={{ padding: "10px 14px", color: "#6b7280" }}>{b.n}</td>
                        <td style={{ padding: "10px 14px", color: "#6b7280" }}>%{b.implied}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 600, color: sideInfo.color }}>%{b.real}</td>
                        <td style={{ padding: "10px 14px" }}><SapmaTag val={b.sapma} /></td>
                        <td style={{ padding: "10px 14px", minWidth: 140 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <DistBar H={b.dist_H} D={b.dist_D} A={b.dist_A} highlight={highlightFTR} />
                            <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
                              {b.dist_H}% / {b.dist_D}% / {b.dist_A}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ════ TAB: Yön Analizi ════ */}
          {activeTab === "direction" && (
            <div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14 }}>
                Piyasa favorisine göre gerçek sonuç dağılımı. Beraberlik ve tersine sonuçların oranını gösterir.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 14 }}>
                {Object.entries(data.direction_analysis).map(([fav, d]) => {
                  const favInfo = SIDES.find(s => s.value === fav);
                  const ftrH = { home: "H", draw: "D", away: "A" }[fav];
                  return (
                    <div key={fav} style={{ background: "#fff", border: `2px solid ${favInfo.color}20`,
                      borderRadius: 8, padding: "16px 20px" }}>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                        Piyasa favorisi:
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: favInfo.color, marginBottom: 12 }}>
                        {favInfo.label}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>n = {d.n} maç</div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 13 }}>
                        <span>Piyasa: <strong>%{d.implied_win}</strong></span>
                        <span>Gerçek: <strong style={{ color: favInfo.color }}>%{d.real_win}</strong></span>
                        <SapmaTag val={d.sapma} />
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <DistBar H={d.dist_H} D={d.dist_D} A={d.dist_A} highlight={ftrH} />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, textAlign: "center" }}>
                        {[["H","Ev","#16a34a"],["D","Ber","#d97706"],["A","Dep","#2563eb"]].map(([k, lbl, col]) => (
                          <div key={k} style={{ background: ftrH === k ? `${col}15` : "#f9fafb",
                            border: ftrH === k ? `1px solid ${col}40` : "1px solid #e5e7eb",
                            borderRadius: 6, padding: "8px 4px" }}>
                            <div style={{ fontSize: 10, color: "#9ca3af" }}>{lbl}</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: ftrH === k ? col : "#374151" }}>
                              %{d[`dist_${k}`]}
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Beraberlik oranı vurgusu */}
                      {fav !== "draw" && d.dist_D >= 25 && (
                        <div style={{ marginTop: 10, background: "#fffbeb", border: "1px solid #fcd34d",
                          borderRadius: 4, padding: "6px 10px", fontSize: 11, color: "#92400e" }}>
                          ⚠ %{d.dist_D} beraberlik oranı yüksek
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ════ TAB: Takım Sapmaları ════ */}
          {activeTab === "teams" && (
            <div>
              {/* Kontroller */}
              <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  placeholder="Takım ara..."
                  value={teamFilter}
                  onChange={e => setTeamFilter(e.target.value)}
                  style={{ fontSize: 13, padding: "6px 12px", border: "1px solid #e5e7eb",
                    borderRadius: 6, width: 180 }}
                />
                <div style={{ display: "flex", gap: 4 }}>
                  <span style={{ fontSize: 12, color: "#6b7280", alignSelf: "center" }}>Sırala:</span>
                  {[
                    ["sapma_abs", "En büyük sapma"],
                    ["sapma",     "Piyasa hafife alıyor"],
                    ["sapma_neg", "Piyasa aşırı fiyatlamış"],
                    ["n",         "Maç sayısı"],
                  ].map(([key, lbl]) => (
                    <button key={key} onClick={() => setTeamSort(key)} style={{
                      padding: "4px 10px", fontSize: 11, borderRadius: 4, cursor: "pointer",
                      border: `1px solid ${teamSort === key ? sideInfo.color : "#e5e7eb"}`,
                      background: teamSort === key ? sideInfo.color : "#fff",
                      color: teamSort === key ? "#fff" : "#6b7280",
                      fontWeight: teamSort === key ? 700 : 400,
                    }}>{lbl}</button>
                  ))}
                </div>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>{sortedTeams.length} takım</span>
              </div>

              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      {["Takım","N","Piyasa%","Gerçek%","Sapma","Sonuç Dağılımı","Sezon Trendi"].map((h, i) => (
                        <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11,
                          color: "#6b7280", letterSpacing: 0.8, textTransform: "uppercase",
                          borderBottom: "1px solid #e5e7eb", fontWeight: 500,
                          whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTeams.map((t, i) => (
                      <tr key={t.team} style={{ borderBottom: "1px solid #f3f4f6",
                        background: Math.abs(t.sapma) >= 5
                          ? (t.sapma > 0 ? "#f0fdf4" : "#fef2f2")
                          : i % 2 === 1 ? "#fafafa" : "#fff" }}>
                        <td style={{ padding: "9px 14px", fontWeight: 600 }}>{t.team}</td>
                        <td style={{ padding: "9px 14px", color: "#6b7280" }}>{t.n}</td>
                        <td style={{ padding: "9px 14px", color: "#6b7280" }}>%{t.implied}</td>
                        <td style={{ padding: "9px 14px", fontWeight: 600, color: sideInfo.color }}>%{t.real}</td>
                        <td style={{ padding: "9px 14px" }}><SapmaTag val={t.sapma} /></td>
                        <td style={{ padding: "9px 14px", minWidth: 120 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <DistBar H={t.dist_H} D={t.dist_D} A={t.dist_A} highlight={highlightFTR} />
                            <span style={{ fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap" }}>
                              {t.dist_H}/{t.dist_D}/{t.dist_A}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: "9px 14px" }}>
                          <MiniTrend trend={t.trend} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {!data && !loading && !error && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 14 }}>Lig ve taraf seçip "Analiz Et" butonuna bas</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>18.000+ maç · 5 sezon · 10 lig · Bet365 + Avg odds</div>
        </div>
      )}
    </div>
  );
}
